// src/rms/pricing/rateEngine.js
// ============================================================================
// Rate rule evaluation
// ============================================================================
// Turns a base rate into a quote by applying the operator's rate rules. Pure
// functions with no I/O, so the whole thing is unit-testable and the same code
// runs for a counter quote, a calendar preview and an invoice.
//
// Rules never mutate the asset's base rate. A quote records which rules fired
// and what each contributed, so a customer asking "why is it $340?" gets a real
// answer instead of a number.
//
// Evaluation order:
//   1. Filter to rules whose scope, travel window, search window, weekday and
//      utilisation band all match
//   2. Sort by priority (low first), then creation order
//   3. Apply in order to a running subtotal; an exclusive rule stops the chain
// ============================================================================

const MS = { hour: 36e5, half_day: 432e5, day: 864e5, week: 6048e5, month: 2592e6 };

/** Billable units between two instants, rounded up. */
export function billableUnits(startsAt, endsAt, rateUnit) {
  if (!endsAt) return 1;
  const ms = new Date(endsAt).getTime() - new Date(startsAt).getTime();
  if (ms <= 0) return 1;
  return Math.max(1, Math.ceil(ms / (MS[rateUnit] || MS.day)));
}

/** ISO weekday, 1 = Monday … 7 = Sunday. */
function isoWeekday(date) {
  const d = date.getDay();
  return d === 0 ? 7 : d;
}

/**
 * Rules store seasonal windows as "MM-DD" so they recur every year, but may
 * also carry a full "YYYY-MM-DD" for a one-off event. Normalise both to a
 * comparable value for the year in question.
 */
function withinAnnualWindow(date, startsOn, endsOn) {
  if (!startsOn && !endsOn) return true;

  const monthDay = `${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const full = date.toISOString().slice(0, 10);

  const norm = (v) => (v && v.length > 5 ? v.slice(5) : v);
  const s = norm(startsOn);
  const e = norm(endsOn);

  // Explicit full dates: compare directly.
  if (startsOn && startsOn.length > 5 && endsOn && endsOn.length > 5) {
    return full >= startsOn && full <= endsOn;
  }

  if (s && e) {
    // A window that wraps the new year, e.g. 12-01 → 03-01.
    if (s > e) return monthDay >= s || monthDay <= e;
    return monthDay >= s && monthDay <= e;
  }
  if (s) return monthDay >= s;
  return monthDay <= e;
}

/** Does this rule target this asset? */
function scopeMatches(rule, asset, group) {
  switch (rule.scope_type) {
    case "all":
      return true;
    case "category":
      return (rule.scope_values || []).includes(asset.category);
    case "group":
      return !!group && (rule.scope_values || []).includes(group.id);
    case "asset":
      return (rule.scope_values || []).includes(asset.id);
    default:
      return false;
  }
}

/**
 * Filters the operator's rules to those applicable to this quote.
 *
 * `searchDate` is when the customer is booking — deliberately separate from the
 * travel dates. "10% off if you book on Black Friday, whenever you travel" is a
 * search-window rule and cannot be expressed as a seasonal one.
 */
export function applicableRules({ rules, asset, group, startsAt, endsAt, searchDate = new Date(), utilizationPct = null }) {
  const start = new Date(startsAt);

  return (rules || [])
    .filter((r) => r.is_active !== false)
    .filter((r) => scopeMatches(r, asset, group))
    .filter((r) => withinAnnualWindow(start, r.travel_starts_on, r.travel_ends_on))
    .filter((r) => {
      if (!r.search_starts_on && !r.search_ends_on) return true;
      return withinAnnualWindow(new Date(searchDate), r.search_starts_on, r.search_ends_on);
    })
    .filter((r) => {
      const days = r.days_of_week || [];
      if (!days.length) return true;
      // Matches if ANY day of the rental falls on a listed weekday.
      const end = endsAt ? new Date(endsAt) : new Date(start.getTime() + 864e5);
      for (let t = start.getTime(); t < end.getTime(); t += 864e5) {
        if (days.includes(isoWeekday(new Date(t)))) return true;
      }
      return false;
    })
    .filter((r) => {
      if (r.utilization_min_pct == null && r.utilization_max_pct == null) return true;
      if (utilizationPct == null) return false;
      if (r.utilization_min_pct != null && utilizationPct < r.utilization_min_pct) return false;
      if (r.utilization_max_pct != null && utilizationPct > r.utilization_max_pct) return false;
      return true;
    })
    .sort((a, b) => (a.priority ?? 100) - (b.priority ?? 100) || String(a.created_at).localeCompare(String(b.created_at)));
}

/** Applies one adjustment to a subtotal, returning the delta in cents. */
function adjustmentDelta(subtotalCents, direction, kind, value) {
  const v = Number(value) || 0;
  if (kind === "set") return Math.round(v * 100) - subtotalCents;

  const magnitude = kind === "percent" ? Math.round((subtotalCents * v) / 100) : Math.round(v * 100);
  return direction === "decrease" ? -magnitude : magnitude;
}

/** Highest tier whose minUnits the rental meets. */
function matchingTier(tiers, units) {
  return (tiers || [])
    .filter((t) => units >= (t.minUnits ?? t.min_units ?? 0))
    .filter((t) => {
      const max = t.maxUnits ?? t.max_units;
      return max == null || units <= max;
    })
    .sort((a, b) => (b.minUnits ?? b.min_units ?? 0) - (a.minUnits ?? a.min_units ?? 0))[0] || null;
}

/**
 * Builds a full quote.
 *
 * Returns the base, an ordered list of applied rule lines (each with its own
 * delta), the final subtotal, and any minimum-duration violation — so the UI
 * can both show the breakdown and block a booking that is too short.
 */
export function buildQuote({
  asset,
  group = null,
  rules = [],
  startsAt,
  endsAt,
  searchDate = new Date(),
  utilizationPct = null,
}) {
  const rateUnit = asset.rate_unit || group?.rate_unit || "day";
  const unitCents = asset.base_rate_cents ?? group?.base_rate_cents ?? 0;
  const units = billableUnits(startsAt, endsAt, rateUnit);

  const baseCents = units * unitCents;
  let subtotal = baseCents;
  const lines = [];
  let minimumUnits = null;

  const candidates = applicableRules({ rules, asset, group, startsAt, endsAt, searchDate, utilizationPct });

  for (const rule of candidates) {
    // Enforces a floor without touching price.
    if (rule.category === "minimum_duration") {
      if (rule.min_duration_units && units < rule.min_duration_units) {
        minimumUnits = Math.max(minimumUnits || 0, rule.min_duration_units);
      }
      continue;
    }

    // Both branches below assign before use; initialising here would be dead.
    let delta;
    let label = rule.name;

    if (rule.category === "length_of_rental" || rule.category === "day_within_rental") {
      const tier = matchingTier(rule.tiers, units);
      if (!tier) continue;
      delta = adjustmentDelta(subtotal, tier.direction, tier.kind, tier.value);
      label = `${rule.name} (${units}+ ${rateUnit.replace("_", " ")})`;
    } else {
      if (!rule.adjustment_kind) continue;
      delta = adjustmentDelta(subtotal, rule.adjustment_direction, rule.adjustment_kind, rule.adjustment_value);
    }

    if (delta === 0) continue;

    subtotal += delta;
    lines.push({
      ruleId: rule.id,
      name: label,
      category: rule.category,
      deltaCents: delta,
    });

    if (rule.is_exclusive) break;
  }

  // A stack of discounts must never produce a negative rental.
  subtotal = Math.max(0, subtotal);

  /**
   * The rate one unit actually costs, and the subtotal made to agree with it.
   *
   * Rules are percentages of a whole booking, so they routinely land on a
   * subtotal no whole number of cents divides into: three days at 7500 less a
   * 0.5% adjustment is 22391, and 22391/3 is 7463.67. A reservation shows the
   * rate rather than the rule stack, so that third of a cent has to go
   * somewhere, and it can only go into the rate or into the total.
   *
   * It goes into the total. An invoice where the printed arithmetic does not
   * add up costs more in queries than it saves in cents, and the reservation
   * total, the invoice line and the ledger entry all derive from this figure,
   * so they agree with the document by construction rather than by luck.
   *
   * The drift is bounded by half a cent per unit -- a cent on three days,
   * about fifteen on a month -- and always against the rules, never against
   * the customer's expectation of what they were quoted.
   *
   * rounding_cents is kept because a discrepancy nobody can name is the kind
   * that gets "fixed" later by someone who does not know it was deliberate.
   */
  const effectiveUnitCents = units > 0 ? Math.round(subtotal / units) : 0;
  const footedSubtotal = units > 0 ? effectiveUnitCents * units : subtotal;
  const roundingCents = footedSubtotal - subtotal;

  return {
    rate_unit: rateUnit,
    units,
    unit_cents: unitCents,
    effective_unit_cents: effectiveUnitCents,
    base_cents: baseCents,
    rule_lines: lines,
    rounding_cents: roundingCents,
    subtotal_cents: footedSubtotal,
    deposit_cents: asset.deposit_cents ?? group?.deposit_cents ?? 0,
    minimum_units: minimumUnits,
    meets_minimum: minimumUnits === null || units >= minimumUnits,
  };
}

// ----------------------------------------------------------------------------
// Reservation deposit — the greater of a percentage and a floor
// ----------------------------------------------------------------------------

export function reservationDepositCents(asset, quotedTotalCents) {
  const pct = asset.reservation_deposit_pct;
  const flat = asset.reservation_deposit_cents;
  if (pct == null && flat == null) return 0;

  const fromPct = pct == null ? 0 : Math.round((quotedTotalCents * Number(pct)) / 100);
  return Math.max(fromPct, flat || 0);
}

// ----------------------------------------------------------------------------
// Usage fees — tiered allowances
// ----------------------------------------------------------------------------

/**
 * Charge for usage beyond the included allowance.
 *
 * Tiers are absolute bands over the whole overage, matching how the trade
 * quotes it ("151–10,000 km at $0.32"). Anything past the last tier falls back
 * to flat_cents_per_unit.
 */
export function usageChargeCents(schedule, usedUnits, rentalUnits) {
  if (!schedule || !Number.isFinite(usedUnits) || usedUnits <= 0) return { overage: 0, cents: 0 };

  const included =
    schedule.period === "rental"
      ? Number(schedule.included_per_period || 0)
      : Number(schedule.included_per_period || 0) * Math.max(1, rentalUnits);

  const overage = Math.max(0, usedUnits - included);
  if (overage <= 0) return { overage: 0, cents: 0, included };

  const tiers = (schedule.tiers || [])
    .slice()
    .sort((a, b) => (a.from ?? 0) - (b.from ?? 0));

  if (!tiers.length) {
    return { overage, included, cents: Math.round(overage * (schedule.flat_cents_per_unit || 0)) };
  }

  let remaining = overage;
  let cursor = 0;
  let cents = 0;

  for (const tier of tiers) {
    if (remaining <= 0) break;
    const from = tier.from ?? 0;
    const to = tier.to ?? Infinity;
    const span = Math.max(0, Math.min(to, cursor + remaining) - Math.max(from, cursor));
    if (span > 0) {
      cents += Math.round(span * (tier.cents_per_unit ?? tier.centsPerUnit ?? 0));
      remaining -= span;
      cursor += span;
    }
  }

  // Past the top tier.
  if (remaining > 0) cents += Math.round(remaining * (schedule.flat_cents_per_unit || 0));

  return { overage, included, cents };
}

// ----------------------------------------------------------------------------
// Add-ons
// ----------------------------------------------------------------------------

/** Add-ons in scope for an asset: account-wide, its group's, and its own. */
export function addonsForAsset(addons, asset, group) {
  return (addons || [])
    .filter((a) => a.is_active !== false)
    .filter(
      (a) =>
        (!a.asset_id && !a.asset_group_id) ||
        a.asset_id === asset.id ||
        (group && a.asset_group_id === group.id)
    )
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

/** Line amount for an add-on given rental length and chosen quantity. */
export function addonAmountCents(addon, quantity, rentalUnits) {
  const qty = Math.max(1, quantity || 1);
  const days = Math.max(1, rentalUnits || 1);

  switch (addon.charge_type) {
    case "per_day":
      return addon.price_cents * days;
    case "per_unit":
      return addon.price_cents * qty;
    case "per_day_per_unit":
      return addon.price_cents * qty * days;
    case "once":
    default:
      return addon.price_cents;
  }
}

// ----------------------------------------------------------------------------
// Cancellation
// ----------------------------------------------------------------------------

/** Refund percentage for cancelling `daysBefore` the rental starts. */
export function refundPctFor(policy, daysBefore) {
  if (!policy) return 0;
  const tier = (policy.tiers || [])
    .slice()
    .sort((a, b) => (b.daysBefore ?? b.days_before ?? 0) - (a.daysBefore ?? a.days_before ?? 0))
    .find((t) => daysBefore >= (t.daysBefore ?? t.days_before ?? 0));
  return tier ? tier.refundPct ?? tier.refund_pct ?? 0 : 0;
}

const rateEngine = {
  billableUnits,
  applicableRules,
  buildQuote,
  reservationDepositCents,
  usageChargeCents,
  addonsForAsset,
  addonAmountCents,
  refundPctFor,
};

export default rateEngine;
